import {lazy, Suspense, useContext, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonColors, atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import AtomPopover, {atomPopoverPositions} from '@s-ui/react-atom-popover'
import Context from '@s-ui/react-context'
import {useMountedState} from '@s-ui/react-hooks'
import {
  MoleculeDrawer,
  MoleculeDrawerOverlay,
  moleculeDrawerPlacements,
  moleculeDrawerSizes
} from '@s-ui/react-molecule-drawer'
import {MoleculeModalContent} from '@s-ui/react-molecule-modal'
import {NOTIFICATIONS} from '@adv-mt/mt-notifications-alert'
import IconMoreHoriz from '@adv-mt/mt-svg-icons/lib/MoreHoriz.js'

import CardAlertMenu from '../menu/index.js'
import {useTracking} from '../useTracking.js'

const ModalDeleteAlert = lazy(() => import(/* webpackChunkName: "ModalDeleteAlert" */ '@adv-mt/mt-modal-delete-alert'))
const ModalUpdateAlert = lazy(() => import(/* webpackChunkName: "ModalUpdateAlert" */ '@adv-mt/mt-modal-update-alert'))

const ModalChangeFrequencyAlert = lazy(() =>
  import(/* webpackChunkName: "ModalChangeFrequencyAlert" */ '@adv-mt/mt-modal-change-frequency-alert')
)

const noop = () => {}
const BASE_CLASS = 'mt-CardAlertNavigation'
const MINIMUM_FILTERS_ERROR_CODE = '100'

const CardAlertNavigation = ({
  allowFiltersUpdate,
  filtersProps = {},
  handleClickCTAButton = noop,
  onCloseOnboardingAlert = noop,
  onRefreshAlert = noop,
  pageName,
  setNotificationType = noop,
  setShow = false,
  userAlert = {}
}) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const reference = useRef()
  const getMountedState = useMountedState()
  const isMounted = getMountedState()
  const {CONFLICT: HTTP_STATUS_CODE_CONFLICT} = config.get('COMMON').HTTP_STATUS_CODES
  const {FREQUENCY_NOTIFICATION_DAILY} = config.get('alerts')

  const [selectedId, setSelectedId] = useState('')
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openNavigationAlertMenuDrawer, setOpenNavigationAlertMenuDrawer] = useState(false)
  const [openUpdateModal, setOpenUpdateModal] = useState(false)
  const [openChangeFrequencyModal, setOpenChangeFrequencyModal] = useState(false)

  const {
    alertDeleted,
    alertDeletedViewed,
    alertEditedError,
    alertEditedSuccess,
    alertEditViewedFromDeleteButton,
    alertEditViewedFromUpdateButton,
    changeFrequencyNotificationDone,
    changeFrequencyViewedFromDeleteButton,
    changeFrequencyViewedFromEditButton
  } = useTracking()

  const {alertId, filters, frequencyNotification} = userAlert

  const handleOpenDeleteModal = alertId => {
    setOpenNavigationAlertMenuDrawer(false)
    setSelectedId(alertId)
    setOpenDeleteModal(true)
    alertDeletedViewed()
  }

  const handleOpenUpdateModalFromEditButton = alertId => {
    setOpenNavigationAlertMenuDrawer(false)
    setSelectedId(alertId)
    setOpenUpdateModal(true)
    alertEditViewedFromUpdateButton()
  }

  const handleOpenUpdateModalFromDeleteModal = alertId => {
    setSelectedId(alertId)
    setOpenUpdateModal(true)
    alertEditViewedFromDeleteButton()
  }

  const closeDeleteModal = () => setOpenDeleteModal(false)

  const deleteUserAlert = async ({alertId}) => {
    const [deletedAlertError] = await domain.get('delete_user_alert_use_case').execute({
      alertId
    })

    setShow(true)
    setNotificationType(deletedAlertError ? NOTIFICATIONS.DELETED_ERROR : NOTIFICATIONS.DELETED_SUCCESS)
  }

  const onConfirmDeleteUserAlert = async () => {
    await deleteUserAlert({
      alertId: selectedId
    })
    closeDeleteModal()
    alertDeleted()
    onRefreshAlert()
  }

  const updatedUserAlertStatus = error => {
    const isDuplicateError = error && error?.status === HTTP_STATUS_CODE_CONFLICT
    const isSearchHasNoFiltersError = error && error?.code === MINIMUM_FILTERS_ERROR_CODE

    const getError = () => {
      if (isDuplicateError) {
        return NOTIFICATIONS.UPDATED_DUPLICATE
      }
      if (isSearchHasNoFiltersError) {
        return NOTIFICATIONS.UPDATED_ERROR_MINIMUM_FILTERS
      }
      return NOTIFICATIONS.UPDATED_ERROR
    }

    onRefreshAlert()
    setShow(true)
    setNotificationType(!error ? NOTIFICATIONS.UPDATED_SUCCESS : getError())

    error && alertEditedError({error})
  }

  const onUpdateFiltersUserAlert = async updatedUserAlert => {
    const {filtersChanged, userAlert} = updatedUserAlert

    const {activeEmail, activePush} = userAlert

    const [updatedFiltersAlertError] = await domain.get('update_user_alert_use_case').execute({
      userAlert,
      activeEmail,
      activePush
    })

    updatedUserAlertStatus(updatedFiltersAlertError)

    !updatedFiltersAlertError && alertEditedSuccess({filtersChanged})
  }

  const closeUpdateModal = () => setOpenUpdateModal(false)

  const onConfirmUpdateUserAlert = async userAlert => {
    await onUpdateFiltersUserAlert(userAlert)
    closeUpdateModal()
    onRefreshAlert()
  }

  const handleOpenChangeFrequencyModal = alertId => {
    setSelectedId(alertId)
    setOpenChangeFrequencyModal(true)
    changeFrequencyViewedFromEditButton()
  }

  const handleOpenChangeFrequencyModalFromDeleteModal = alertId => {
    setSelectedId(alertId)
    setOpenChangeFrequencyModal(true)
    changeFrequencyViewedFromDeleteButton()
  }

  const handleChangeFrequency = async ({userAlert, frequencyData}) => {
    const {frequencyNotification} = userAlert

    const [changeFrequencyError] = await domain.get('update_frequency_user_alert_use_case').execute({
      userAlert,
      frequencyData
    })

    setShow(true)
    changeFrequencyNotificationDone({
      frequencyData,
      changeFrequencyError,
      previousFrequency: frequencyNotification
    })

    if (changeFrequencyError) {
      setNotificationType(NOTIFICATIONS.CHANGED_FREQUENCY_ERROR)
    } else {
      setNotificationType(
        frequencyData.value === FREQUENCY_NOTIFICATION_DAILY
          ? NOTIFICATIONS.CHANGED_FREQUENCY_TO_DAILY_SUCCESS
          : NOTIFICATIONS.CHANGED_FREQUENCY_TO_REALTIME_SUCCESS
      )
    }
  }

  const closeChangeFrequencyModal = () => setOpenChangeFrequencyModal(false)

  const onConfirmChangeFrequencyUserAlert = async ({userAlert, frequencyData}) => {
    await handleChangeFrequency({userAlert, frequencyData})
    closeChangeFrequencyModal()
    onRefreshAlert()
  }

  return (
    <>
      {isMobile ? (
        <div className={`${BASE_CLASS}-header--menu`}>
          <AtomButton
            design={atomButtonDesigns.FLAT}
            color={atomButtonColors.PRIMARY}
            leftIcon={<IconMoreHoriz size={ATOM_ICON_SIZES.medium} />}
            shape={atomButtonShapes.ROUNDED}
            onClick={() => {
              setOpenNavigationAlertMenuDrawer(!openNavigationAlertMenuDrawer)
              onCloseOnboardingAlert()
            }}
          ></AtomButton>
          {openNavigationAlertMenuDrawer && (
            <Suspense fallback={<></>}>
              <MoleculeDrawerOverlay isVisible={openNavigationAlertMenuDrawer}>
                <MoleculeDrawer
                  closeOnOutsideClick
                  isOpen={openNavigationAlertMenuDrawer}
                  onClose={() => setOpenNavigationAlertMenuDrawer(false)}
                  placement={moleculeDrawerPlacements.BOTTOM}
                  ref={reference}
                  size={moleculeDrawerSizes.AUTO}
                >
                  <MoleculeModalContent>
                    <CardAlertMenu
                      allowFiltersUpdate={allowFiltersUpdate}
                      onClickChangeFrequency={() => handleOpenChangeFrequencyModal(alertId)}
                      onClickCTAButton={() => handleClickCTAButton({filters, alertId})}
                      onClickDelete={() => handleOpenDeleteModal(alertId)}
                      onClickUpdateFilters={() => handleOpenUpdateModalFromEditButton(alertId)}
                    />
                  </MoleculeModalContent>
                </MoleculeDrawer>
              </MoleculeDrawerOverlay>
            </Suspense>
          )}
        </div>
      ) : (
        <AtomPopover
          content={() => (
            <CardAlertMenu
              allowFiltersUpdate={allowFiltersUpdate}
              frequencyNotification={frequencyNotification}
              onClickChangeFrequency={() => handleOpenChangeFrequencyModal(alertId)}
              onClickCTAButton={() => handleClickCTAButton({filters, alertId})}
              onClickDelete={() => handleOpenDeleteModal(alertId)}
              onClickUpdateFilters={() => handleOpenUpdateModalFromEditButton(alertId)}
            />
          )}
          hideArrow
          placement={atomPopoverPositions.BOTTOM_END}
          type="system"
        >
          <div className={`${BASE_CLASS}-header--menu`}>
            <AtomButton
              design={atomButtonDesigns.FLAT}
              color={atomButtonColors.PRIMARY}
              leftIcon={<IconMoreHoriz size={ATOM_ICON_SIZES.medium} />}
              shape={atomButtonShapes.ROUNDED}
              onClick={() => {
                setOpenNavigationAlertMenuDrawer(!openNavigationAlertMenuDrawer)
                onCloseOnboardingAlert()
              }}
            ></AtomButton>
          </div>
        </AtomPopover>
      )}
      {openUpdateModal && isMounted && (
        <Suspense fallback={<></>}>
          <ModalUpdateAlert
            i18n={i18n}
            isOpen={openUpdateModal}
            filtersProps={filtersProps}
            onClose={closeUpdateModal}
            onConfirm={onConfirmUpdateUserAlert}
            pageName={pageName}
            updateData={userAlert}
          />
        </Suspense>
      )}
      {openChangeFrequencyModal && isMounted && (
        <Suspense fallback={<></>}>
          <ModalChangeFrequencyAlert
            isOpen={openChangeFrequencyModal}
            onClose={closeChangeFrequencyModal}
            onConfirm={onConfirmChangeFrequencyUserAlert}
            currentUserAlert={userAlert}
          />
        </Suspense>
      )}
      {openDeleteModal && isMounted && (
        <Suspense fallback={<></>}>
          <ModalDeleteAlert
            allowFiltersUpdate={allowFiltersUpdate}
            currentUserAlert={userAlert}
            isOpen={openDeleteModal}
            onUpdate={() => {
              closeDeleteModal()
              handleOpenUpdateModalFromDeleteModal(alertId)
            }}
            onClose={closeDeleteModal}
            onConfirm={onConfirmDeleteUserAlert}
            onChangeFrequency={() => {
              closeDeleteModal()
              handleOpenChangeFrequencyModalFromDeleteModal(alertId)
            }}
          />
        </Suspense>
      )}
    </>
  )
}

CardAlertNavigation.displayName = 'CardAlertNavigation'

CardAlertNavigation.propTypes = {
  allowFiltersUpdate: PropTypes.bool.isRequired,
  filtersProps: PropTypes.object,
  handleClickCTAButton: PropTypes.func,
  onCloseOnboardingAlert: PropTypes.func,
  onRefreshAlert: PropTypes.func,
  pageName: PropTypes.string,
  userAlert: PropTypes.object,
  setNotificationType: PropTypes.func,
  setShow: PropTypes.func
}

export default CardAlertNavigation
